import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  QuestionMark as QuestionIcon,
  Description as LeafIcon,
  CalendarMonth as SessionIcon,
  NavigateNext as NavigateNextIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SetupNode, SetupNodeSession } from '../types';
import sessionService from '../services/session.service';
import { useSetupStore } from '../store/setupStore';
import { toast } from 'react-toastify';

// Helper function to extract the question part from node text
// Looks for patterns like "Quel...", "Quelle...", "En quelle...", etc.
const extractQuestion = (text: string): string => {
  // Common question patterns in French
  const questionPatterns = [
    /\b(Quel(?:le)?(?:s)?)\s+.+\?/i,
    /\b(En quelle)\s+.+\?/i,
    /\b(Quel est)\s+.+\?/i,
    /\b(Quelle est)\s+.+\?/i,
  ];

  for (const pattern of questionPatterns) {
    const match = text.match(pattern);
    if (match) {
      // Return from the match to the end (including ?)
      const startIndex = text.indexOf(match[0]);
      return text.substring(startIndex);
    }
  }

  // If no question pattern found, check if text ends with ?
  if (text.includes('?')) {
    // Find the last sentence that ends with ?
    const sentences = text.split(/(?<=[.!])\s+/);
    for (let i = sentences.length - 1; i >= 0; i--) {
      if (sentences[i].includes('?')) {
        return sentences[i].trim();
      }
    }
  }

  // Return full text if no question found
  return text;
};

// Helper function to extract the statement part (before the question)
const extractStatement = (text: string): string => {
  // Common question patterns in French
  const questionPatterns = [
    /\b(Quel(?:le)?(?:s)?)\s+.+\?/i,
    /\b(En quelle)\s+.+\?/i,
    /\b(Quel est)\s+.+\?/i,
    /\b(Quelle est)\s+.+\?/i,
  ];

  for (const pattern of questionPatterns) {
    const match = text.match(pattern);
    if (match) {
      const startIndex = text.indexOf(match[0]);
      if (startIndex > 0) {
        return text.substring(0, startIndex).trim();
      }
    }
  }

  // If text contains ?, try to split at the last sentence before ?
  if (text.includes('?')) {
    const questionIndex = text.lastIndexOf('?');
    // Find the start of the question sentence
    const beforeQuestion = text.substring(0, questionIndex);
    const lastPeriod = Math.max(
      beforeQuestion.lastIndexOf('.'),
      beforeQuestion.lastIndexOf('!')
    );
    if (lastPeriod > 0) {
      return text.substring(0, lastPeriod + 1).trim();
    }
  }

  // Return full text if no question found
  return text;
};

interface SetupNavigationModalProps {
  open: boolean;
  onClose: () => void;
  onSessionSelect: (sessionId: number) => void;
}

const SetupNavigationModal: React.FC<SetupNavigationModalProps> = ({
  open,
  onClose,
  onSessionSelect,
}) => {
  // Use the setup store for caching
  const { activeSetup: setup, isLoading: loading, loadActiveSetup } = useSetupStore();

  const [path, setPath] = useState<SetupNode[]>([]);
  const [currentNode, setCurrentNode] = useState<SetupNode | null>(null);
  const [sessionPreviewUrl, setSessionPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewSessionName, setPreviewSessionName] = useState<string>('');

  // Load active setup from cache (or fetch if not cached)
  useEffect(() => {
    const initSetup = async () => {
      if (!open) return;

      try {
        // loadActiveSetup uses cache if available
        const activeSetup = await loadActiveSetup();
        if (activeSetup && activeSetup.nodes && activeSetup.nodes.length > 0) {
          const root = activeSetup.nodes[0];
          setCurrentNode(root);
          setPath([root]);
        }
      } catch (error) {
        console.error('Error loading active setup:', error);
        toast.error('Erreur lors du chargement du setup');
      }
    };

    initSetup();
  }, [open, loadActiveSetup]);

  // Navigate to a child node
  const handleSelectChild = (child: SetupNode) => {
    setPath([...path, child]);
    setCurrentNode(child);
  };

  // Go back to parent
  const handleGoBack = () => {
    if (path.length > 1) {
      const newPath = path.slice(0, -1);
      setPath(newPath);
      setCurrentNode(newPath[newPath.length - 1]);
    }
  };

  // Navigate to breadcrumb
  const handleBreadcrumbClick = (index: number) => {
    const newPath = path.slice(0, index + 1);
    setPath(newPath);
    setCurrentNode(newPath[newPath.length - 1]);
  };

  // View session preview
  const handleViewSession = async (ns: SetupNodeSession) => {
    setLoadingPreview(true);
    setPreviewSessionName(ns.session_name);
    try {
      const pdfUrl = await sessionService.generatePreview(ns.session);
      setSessionPreviewUrl(pdfUrl);
    } catch (error) {
      console.error('Error loading session preview:', error);
      toast.error('Erreur lors du chargement de la prévisualisation');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Use session (add to timeline)
  const handleUseSession = (sessionId: number) => {
    onSessionSelect(sessionId);
    onClose();
  };

  // Close preview
  const handleClosePreview = () => {
    if (sessionPreviewUrl) {
      URL.revokeObjectURL(sessionPreviewUrl);
    }
    setSessionPreviewUrl(null);
    setPreviewSessionName('');
  };

  // Reset on close
  const handleClose = () => {
    if (sessionPreviewUrl) {
      URL.revokeObjectURL(sessionPreviewUrl);
    }
    setSessionPreviewUrl(null);
    setPath([]);
    setCurrentNode(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>Setup de séance</Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : !setup ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun setup actif
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allez dans Atelier &gt; Setups pour créer ou activer un setup.
              </Typography>
            </Box>
          ) : !currentNode ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                Ce setup ne contient aucun nœud.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Breadcrumbs */}
              {path.length > 1 && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <IconButton onClick={handleGoBack} size="small" sx={{ mr: 1 }}>
                    <BackIcon />
                  </IconButton>
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                    {path.map((node, index) => (
                      <Link
                        key={node.id}
                        component="button"
                        variant="body2"
                        underline="hover"
                        color={index === path.length - 1 ? 'text.primary' : 'inherit'}
                        onClick={() => handleBreadcrumbClick(index)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {node.text.length > 30 ? `${node.text.substring(0, 30)}...` : node.text}
                      </Link>
                    ))}
                  </Breadcrumbs>
                </Box>
              )}

              {/* Current node - show only the question part */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: currentNode.node_type === 'question' ? '#fff3e0' : '#e8f5e9' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {currentNode.node_type === 'question' ? (
                    <QuestionIcon color="warning" />
                  ) : (
                    <LeafIcon color="success" />
                  )}
                  <Typography variant="h6">
                    {currentNode.node_type === 'question'
                      ? extractQuestion(currentNode.text)
                      : currentNode.text}
                  </Typography>
                </Box>
              </Paper>

              {/* Children (for questions) - show only the statement part */}
              {currentNode.node_type === 'question' && currentNode.children && currentNode.children.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Choisissez une réponse :
                  </Typography>
                  <List>
                    {currentNode.children.map((child) => (
                      <ListItem key={child.id} disablePadding>
                        <ListItemButton onClick={() => handleSelectChild(child)}>
                          <ListItemIcon>
                            {child.node_type === 'question' ? (
                              <QuestionIcon color="warning" />
                            ) : (
                              <LeafIcon color="success" />
                            )}
                          </ListItemIcon>
                          <ListItemText primary={extractStatement(child.text) || child.text} />
                          <NavigateNextIcon color="action" />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* No children for question */}
              {currentNode.node_type === 'question' && (!currentNode.children || currentNode.children.length === 0) && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  Aucune réponse définie pour cette question.
                </Typography>
              )}

              {/* Sessions (for leaves) */}
              {currentNode.node_type === 'leaf' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Séances proposées :
                  </Typography>
                  {currentNode.node_sessions && currentNode.node_sessions.length > 0 ? (
                    <List>
                      {currentNode.node_sessions.map((ns) => (
                        <ListItem key={ns.id} disablePadding>
                          <ListItemButton onClick={() => handleViewSession(ns)}>
                            <ListItemIcon>
                              <SessionIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={ns.session_name} />
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PdfIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewSession(ns);
                              }}
                              sx={{ mr: 1 }}
                            >
                              Voir
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUseSession(ns.session);
                              }}
                            >
                              Utiliser
                            </Button>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                      Aucune séance associée à cette feuille.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Session Preview Dialog */}
      <Dialog
        open={!!sessionPreviewUrl || loadingPreview}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {previewSessionName || 'Prévisualisation de la séance'}
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : sessionPreviewUrl ? (
            <iframe
              src={sessionPreviewUrl}
              style={{ width: '100%', height: '600px', border: 'none' }}
              title="Session Preview"
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetupNavigationModal;
